import React, { useState, useRef, useCallback } from "react";
import { useMemo, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useQuery } from "../Hooks";
import { useTheme } from "styled-components";
import { Flex } from "reflexbox";
import { PageWrapper } from "../UI/Grid";
import Button from "../UI/Button";
import Lines from "./Lines";
// import Details from "./Details";
import Customer from "./Customer";
import Attachments from "./Attachments";
// import Communications from "./Communications";
import Summary from "./Summary";
import formatter from "../../modules/formatter";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import * as qRs from "../../requests/quoteManagementRequests";
import { createRMAFromQuote } from "../../requests/RMAManagementRequests";
import Loading from "../UI/Loading";
import { getCustomer } from "../../requests/customerManagementRequests";
import HeadingSection from "../UI/HeadingSection";
import { Body } from "../UI/Text";
import AlertContext from "../../context/alert/AlertContext";
import UserContext from "../../context/user/UserContext";
import { InteractionsTable } from "../Interactions";
import Modal from "../UI/Modal";
import PagerContextProvider from "../UI/Pager/PagerContext";
import Card from "../UI/Card";
import _ from "lodash";
import ShippingInfo from "./ShippingInfo";

const { getQuote, getCommunications } = qRs;

const Quote = ({ isDealer: _isDealer = false }) => {
	const { quoteId } = useParams();
	const theme = useTheme();
	const navigate = useNavigate();

	const { setQuote, quote, setPrinting } = useContext(ConfiguratorContext);
	const { showFailureAlert } = useContext(AlertContext);
	const {
		isLoading: userLoading,
		activeUser,
		platform,
	} = useContext(UserContext);
	const { allowRMAs, showShippingTracking } = platform || {};

	const lines = useMemo(() => quote?.lines || [], [quote]);

	const [show, setShow] = useState(false);
	const [showInteractions, setShowInteractions] = useState(false);
	const [showShippingInfo, setShowShippingInfo] = useState(false);

	const [preview, setPreview] = useState(false);

	// Get Communications
	const _comms = useQuery({
		queryID: `comms|${quoteId}`,
		skip: !quoteId || userLoading,
		request: getCommunications,
		queryVariables: { quoteId },
		forcedFilters: {},
	});

	// Get Quote
	const _quote = useQuery({
		queryID: `quote|${quoteId}`,
		skip: !quoteId || userLoading || !_comms.data || _comms.isLoading,
		request: getQuote,
		forcedFilters: {},
		queryVariables: { id: +quoteId, isDealer: _isDealer },
	});

	const isDealer = useMemo(
		() => !_quote.data?.isPlane || _isDealer,
		[_quote.data, _isDealer]
	);

	const customerQueryVariables = useMemo(() => {
		if (_quote.data?.contactId) return { id: _quote.data?.contactId };
		else return { quoteId };
	}, [_quote.data, quoteId]);

	const _customer = useQuery({
		queryID: `customer|quote|${quoteId}`,
		request: getCustomer,
		queryVariables: customerQueryVariables,
		skip:
			!quoteId ||
			!_comms.data ||
			_comms.isLoading ||
			!_quote.data ||
			_quote.isLoading ||
			userLoading,
	});

	const { data: customerData, refetchQuery: refetchCustomer } = _customer || {};

	const { customer, contact, billingAddress, shippingAddresses } =
		customerData || {};

	useEffect(() => {
		setQuote({
			..._quote.data,
			customer,
			contact,
			billingAddress,
			shippingAddresses,
		});
	}, [
		billingAddress,
		contact,
		customer,
		_quote.data,
		setQuote,
		shippingAddresses,
	]);

	useEffect(() => {
		if (
			!!quote &&
			quote?.id &&
			quote?.sameAsBilling &&
			quote?.billingAddress &&
			!_quote?.isLoading &&
			quote?.shippingAddress?.id !== quote?.billingAddress?.id
		) {
			setQuote({
				...quote,
				shippingAddress: quote?.billingAddress,
			});
		}
	}, [quote, setQuote, _quote]);

	const printRef = useRef();

	const [stage, setStage] = useState(0);

	// useEffect(() => {
	//   if (!quote) return;
	//   else {
	//     if (quote.type === 2 && stage !== 0) setStage(0);
	//   }
	// }, [quote, quoteId, stage]);

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	});

	const onPrintClick = useCallback(() => {
		setTimeout(() => {
			handlePrint();
			setPrinting(false);
		}, 0);
	}, [handlePrint, setPrinting]);

	const commonProps = useMemo(
		() => ({
			isLoading:
				_quote?.isLoading ||
				_comms.isLoading ||
				userLoading ||
				!_customer.data ||
				!_comms.data ||
				!_quote.data ||
				_customer.isLoading,
			refetchQuote: _quote?.refetchQuery,
		}),
		[_comms, _customer, _quote, userLoading]
	);

	const title = useMemo(() => {
		let type = "Quote";
		if (quote?.type === 2) type = "Order";
		if (quote?.type === 3) type = "Quote Request";

		let status = "Ordered";
		if (quote?.csiStatus === "W") status = "Submitted";
		if (quote?.csiStatus === "C") status = "Complete";

		return `${type} #${
			quote?.type === 2 ? (quote?.erpOrderNo || "").trim() : quote?.id
		}${quote?.type === 2 ? ` "${status}"` : ""}`;
	}, [quote]);

	const isPlane = useMemo(() => !!quote?.isPlane, [quote]);
	const price = useMemo(() => {
		let total = 0;
		let discountTotal = 0;
		let totalDealerPrice = 0;
		if (lines && lines?.length > 0) {
			for (let line of lines) {
				console.log({ line });
				const { quantity = 1, price, dealerPrice, Details: d } = line;
				total += quantity * price?.toFixed(2);
				let discountPrice = !isPlane ? dealerPrice : 0;
				if (!!isDealer && !isPlane) {
					const Details = d ? d : []; //d || []; //

					if (!Details) continue;

					Details.forEach((Detail, i) => {
						const { Value, Caption: _Caption } = Detail || {};

						const isMSRP = _Caption.startsWith("MSRP");
						const isDEALER = _Caption.startsWith("DEALER");

						if (!isMSRP && !isDEALER) return;
						if (Value === "***INCLUDED***") return;

						const val = Value.replace(/[^\p{L}0-9]/g, "") / 100;

						if (isDealer) {
							if (isDEALER) {
								discountPrice += val;
							}
						}
					});
				}
				totalDealerPrice += isPlane
					? discountPrice?.toFixed(2)
					: (line?.price -
							(line?.price * ((line?.discount_percentage * -1) / 100))?.toFixed(
								2
							)) *
					  line?.quantity;
				//discountTotal += ((isPlane ? discountPrice.toFixed(2) : price.toFixed(2) - discountPrice) * quantity);
			}

			if (isDealer)
				return (
					<Flex
						style={{ gap: "0.5rem" }}
						justifyContent="center"
						alignItems="center"
					>
						<Body w="auto" bold>
							Total:
						</Body>
						<Body w="auto" bold right linethrough>
							${formatter.currency(total)}
						</Body>
						<Body w="auto" bold color={theme.secondary}>
							${formatter.currency(totalDealerPrice)}
						</Body>
					</Flex>
				);

			return `Total: $${formatter.currency(total)}`;
		}

		return `Total: $${formatter.currency(0)}`;
	}, [lines, isDealer, theme.secondary, isPlane]);

	const stages = useMemo(
		() =>
			quote && quote?.type !== 2
				? [
						{
							id: "Lines",
							component: (
								<Lines
									title="Lines"
									show={show}
									setShow={setShow}
									{...commonProps}
								/>
							),
						},

						{
							id: "Customer",
							component: (
								<Customer refetchCustomer={refetchCustomer} {...commonProps} />
							),
						},

						{
							id: "Summary",
							component: (
								<Summary
									{...commonProps}
									isDealer={isDealer}
									isPlane={isPlane}
									printRef={printRef}
									preview={preview}
									setPreview={setPreview}
									communications={_comms?.data}
								/>
							),
						},
				  ]
				: [
						{
							id: "Summary",
							component: (
								<Summary
									{...commonProps}
									isDealer={isDealer}
									isPlane={isPlane}
									printRef={printRef}
									preview={preview}
									setPreview={setPreview}
									communications={_comms?.data}
								/>
							),
						},
				  ],
		[
			quote,
			show,
			commonProps,
			refetchCustomer,
			isDealer,
			isPlane,
			preview,
			_comms.data,
		]
	);

	const onSetStage = useCallback(
		(_stage) => {
			setStage(_stage);
			window.sessionStorage.setItem(`quoteId:${quoteId}`, _stage);
		},
		[quoteId]
	);

	const onCreateRMA = useCallback(async () => {
		const { id: rmaId } = await createRMAFromQuote(quoteId);
		if (rmaId) return navigate(`/RMA/${rmaId}`);
		return showFailureAlert("Error creating RMA from Order.");
	}, [navigate, quoteId, showFailureAlert]);
	// const onCreateInteraction = useCallback(async () => {
	//   const { id: rmaId } = await createRMAFromQuote(quoteId);
	//   if (rmaId) return navigate(`/RMA/${rmaId}`);
	//   return showFailureAlert("Error creating RMA from Order.");
	//   setShowInteractions(true);
	// }, [navigate, quoteId, showFailureAlert]);

	const onShowShippingInfo = useCallback(async () => {
		setShowShippingInfo(true);
	}, []);

	const CreateOrderButtons = useMemo(() => {
		return quote?.type === 2 && !isPlane ? (
			<Flex key="create-rma-button">
				{/* <Button
          color={theme.secondary}
          title="Create Interaction"
          iconName="faComments"
          onClick={() => setShowInteractions(true)}
        /> */}
				{allowRMAs && (
					<Button
						title={"Create RMA"}
						iconName="faPersonWalkingArrowLoopLeft"
						onClick={() => onCreateRMA()}
					>
						{"Create RMA"}
					</Button>
				)}

				{showShippingTracking && (
					<Button
						title={"Shipping"}
						iconName="faTruck"
						onClick={() => onShowShippingInfo()}
						color={theme.secondary}
					>
						{"Shipping"}
					</Button>
				)}
			</Flex>
		) : null;
	}, [
		onCreateRMA,
		quote,
		isPlane,
		onShowShippingInfo,
		theme,
		allowRMAs,
		showShippingTracking,
	]);

	const StageSelector = useMemo(
		() =>
			!quote?.type || quote?.type === 2
				? null
				: [
						<Button
							size="small"
							key="left"
							color={theme.secondary}
							reverse
							mr="1rem"
							style={{
								opacity: !stage ? 0 : 1,
								cursor: !stage ? "auto" : "pointer",
								pointerEvents: !stage ? "none" : "",
							}}
							iconName="faAngleLeft"
							onClick={() => {
								if (stage - 1 === -1) return null;
								return onSetStage(stage - 1);
							}}
						/>,
						...stages.map(({ id }, i) => (
							<Button
								size="small"
								className={stage !== i ? "hide" : undefined}
								color={theme.secondary}
								disabled={
									(id === "Summary" && !quote?.contactId) ||
									(!lines && stage !== i)
								}
								reverse={stage !== i}
								key={`${id}_${i}`}
								onClick={() => onSetStage(i)}
							>
								{id}
							</Button>
						)),
						<Button
							size="small"
							key="right"
							color={theme.secondary}
							disabled={(stage === 1 && !quote?.contactId) || !lines}
							reverse
							ml="1rem"
							style={{
								opacity:
									(stage === 1 && !quote?.contactId) || !lines
										? undefined
										: (stage === stages.length - 1 || !stages.length) &&
										  stages.length !== 1
										? 0
										: 1,
								cursor:
									(stage === 1 && !quote?.contactId) || !lines
										? undefined
										: (stage === stages.length - 1 || !stages.length) &&
										  stages.length !== 1
										? "auto"
										: "pointer",
								pointerEvents:
									(stage === stages.length - 1 || !stages.length) &&
									stages.length !== 1
										? "none"
										: "",
							}}
							iconName="faAngleRight"
							onClick={() => {
								if (stage + 1 === stages.length) return null;
								return onSetStage(stage + 1);
							}}
						/>,
				  ],
		[onSetStage, lines, stage, stages, theme.secondary, quote]
	);

	const shipCode = useMemo(() => {
		return _.find(
			quote?.shipCodes || [],
			({ ShipVia }) => ShipVia === quote?.shipCode
		);
	}, [quote]);

	return _quote?.isLoading ||
		_comms.isLoading ||
		userLoading ||
		!_customer.data ||
		!_comms.data ||
		!_quote.data ||
		_customer.isLoading ? (
		<Loading />
	) : (
		<>
			{isDealer && (
				<Flex
					alignItems="center"
					width="100%"
					height="2rem"
					backgroundColor={theme.headerTextColor}
				>
					<Body color={theme.headerColor} center bold>
						Dealer Pricing View
					</Body>
				</Flex>
			)}
			<Modal show={showShippingInfo} setShow={setShowShippingInfo}>
				<PagerContextProvider>
					<Card>
						<ShippingInfo lineDetails={quote?.lineDetails} />
					</Card>
				</PagerContextProvider>
			</Modal>
			<Modal show={showInteractions} setShow={setShowInteractions}>
				<PagerContextProvider defaultOrder={["CreateDate", "DESC"]}>
					<Card>
						<InteractionsTable quoteId={quoteId} groupId={quote?.groupId} />
					</Card>
				</PagerContextProvider>
			</Modal>
			<PageWrapper className="no-print">
				<HeadingSection
					my="1rem"
					hideCreate={stage === 1}
					createLink={
						stages?.[stage]?.id === "Summary"
							? () => {
									setPrinting(true);
									onPrintClick();
							  }
							: () => setShow(true)
					}
					createButtonText={
						stages?.[stage]?.id === "Summary" ? undefined : "Catalog"
					}
					createButtonColor={
						stages?.[stage]?.id === "Summary" ? theme.gray : undefined
					}
					createButtonIcon={
						stages?.[stage]?.id === "Summary" ? "faPrint" : "faCartPlus"
					}
					title={title}
					subtitle={price}
					centerSubtitle
					boldSubtitle
					backButtonLink={
						quote?.type === 2 ? `/MyOrders/${activeUser?.username}` : "/"
					}
					centerTitle
					inlineAdditionalElements
					additionalElements={
						CreateOrderButtons ? [CreateOrderButtons] : undefined
					}
				/>
				{quote?.type === 2 ? (
					<>
						<Flex className="no-print">
							<Body mr="0.5rem" flex={1} right bold>
								Customer PO:
							</Body>
							<Body flex={1} bold color={theme.secondary}>
								{quote?.poNumber}
							</Body>
						</Flex>
						{!!shipCode && !isPlane && (
							<Flex mb="1rem" width={1} justifyContent="space-between">
								<Body right mr="0.5rem" flex={1} bold>
									Shipping Method:
								</Body>
								<Body flex={1} bold color={theme.secondary}>
									{shipCode?.description}
								</Body>
							</Flex>
						)}
					</>
				) : null}
				<Flex mb="1rem" width={1} justifyContent="center">
					{StageSelector}
				</Flex>

				{stages?.[stage]?.component || null}

				{stage === 0 && (
					<Flex
						flexDirection={[
							"column-reverse",
							"column-reverse",
							"column-reverse",
							"row",
						]}
						alignItems="flex-start"
						width={1}
						style={{ gap: "1rem" }}
						pt="1rem"
					>
						{/* <Communications
              communications={_comms?.data}
              setData={_comms?.setData}
            /> */}
						<Attachments
							readonly={quote?.type === 2}
							attachmentType={quote?.type === 2 ? 2 : 1}
						/>
					</Flex>
				)}
			</PageWrapper>
		</>
	);
};

export default Quote;
